import { toChartTicks } from '@/utils/chart-scale';

export type TrendPoint = {
  date: string;
  value: number;
};

export const TREND_VIEWBOX = { width: 640, height: 170 };
export const TREND_DOT_TARGET = 5;

const INSET_X = 20;
const BASELINE_Y = 150;
const PEAK_Y = 25;
const PLOT_HEIGHT = BASELINE_Y - PEAK_Y;

/**
 * Builds the polyline, the closed area path and the dot positions. Dot spacing is
 * derived from the point count rather than fixed, because the bucket count follows
 * the calendar span and ranges from four to three hundred and sixty-six.
 */
export function toTrendGeometry(points: TrendPoint[]) {
  const tallest = Math.max(...points.map((point) => point.value), 0);
  const span = TREND_VIEWBOX.width - INSET_X * 2;
  const step = points.length > 1 ? span / (points.length - 1) : 0;

  if (!points.length) {
    return { line: '', area: '', dots: [] as { x: number; y: number }[], tallest, step };
  }

  const coords = points.map((point, index) => ({
    x: INSET_X + index * step,
    y: BASELINE_Y - (tallest ? (point.value / tallest) * PLOT_HEIGHT : 0),
  }));

  const line = coords.map((coord) => `${coord.x},${coord.y}`).join(' ');
  const lastX = coords[coords.length - 1].x;
  const area = `M${coords.map((coord) => `${coord.x},${coord.y}`).join(' L')} L${lastX},${BASELINE_Y} L${INSET_X},${BASELINE_Y} Z`;

  const every = Math.max(1, Math.ceil(points.length / TREND_DOT_TARGET));
  const dots = coords.filter((_, index) => index % every === 0);

  return { line, area, dots, tallest, step };
}

/**
 * Three gridlines with the values they stand for, plus the tallest point. The line alone
 * shows a shape; these give it a scale without a number per bucket. Reads `tallest` and
 * `step` off the geometry rather than re-deriving them, so the peak label cannot drift
 * away from the dot it names.
 */
export function toTrendScale(
  points: TrendPoint[],
  { tallest, step }: Pick<ReturnType<typeof toTrendGeometry>, 'tallest' | 'step'>
) {
  const ticks = toChartTicks(tallest).map(({ value, fraction }) => ({
    value,
    y: BASELINE_Y - PLOT_HEIGHT * fraction,
  }));

  const peakIndex = points.findIndex((point) => point.value === tallest);

  const peak =
    tallest > 0
      ? { value: tallest, x: INSET_X + peakIndex * step, y: BASELINE_Y - PLOT_HEIGHT }
      : null;

  return { ticks, peak };
}

export { formatBucketLabel as formatTrendLabel } from '@/utils/helpers';
